'use client';

import { useEffect } from 'react';

const shouldReport = process.env.NEXT_PUBLIC_ENABLE_CLIENT_LOGGING !== 'false';

const reportClientError = (payload: Record<string, unknown>) => {
  if (!shouldReport) {
    return;
  }

  const body = JSON.stringify({
    ...payload,
    userAgent: navigator.userAgent,
    href: window.location.href,
    timestamp: new Date().toISOString(),
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon('/api/logs/client', blob);
    return;
  }

  void fetch('/api/logs/client', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // ignore network errors silently
  });
};

export function ClientLogger() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      reportClientError({
        type: 'error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      reportClientError({
        type: 'unhandledrejection',
        reason: event.reason instanceof Error ? event.reason.message : String(event.reason),
        stack: event.reason instanceof Error ? event.reason.stack : undefined,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
